import type { SearchAppData } from '@global-types/searchAppData.ts';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { useLoading } from '@composables/useLoading.ts';
import type { ExtendedAlgoliaOffer } from '@global-types/algoliaOffer';
import { getAlgoliaOffers, getAlgoliaSpacesCount } from '@api/algolia/apiAlgolia';
import type { TransportLine } from '@models/groups/search';
import type { Url } from '@global-types/url.ts';
import type { DeserializedOffer } from '@pages/search/serializer/deserializedOffer';

export const useSearchStore = defineStore('search', () => {
  const searchAppData = ref<SearchAppData>({
    favorite_offers: [],
    transport_lines: [],
  });

  const {
    isLoading,
    startLoading,
    stopLoading,
  } = useLoading();

  const perPage = ref<number>(25);

  const offers = ref<ExtendedAlgoliaOffer[]>([]);
  const nbOffers = ref<number>(0);
  const nbSpaces = ref<number>(0);

  const initSearchData = (): void => {
    const dataElement = document.getElementById('search-data');

    if (!dataElement) {
      return;
    }

    searchAppData.value = JSON.parse(dataElement.textContent) as SearchAppData;
  };

  initSearchData();

  const transportLines = computed<TransportLine[]>(() => searchAppData.value.transport_lines);

  const isFavorite = (hashId: string): boolean => searchAppData.value.favorite_offers.includes(hashId);

  const addFavorite = (hashId: string): void => {
    if (!isFavorite(hashId)) {
      searchAppData.value.favorite_offers.push(hashId);
    }
  };

  const removeFavorite = (hashId: string): void => {
    searchAppData.value.favorite_offers = searchAppData.value.favorite_offers.filter((id) => id !== hashId);
  };

  const fetchSearchData = async (): Promise<void> => {
    startLoading();

    const [ offersData, spacesCount ] = await Promise.all([
      getAlgoliaOffers(),
      getAlgoliaSpacesCount(),
    ]);

    offers.value = offersData?.hits ?? [];
    nbOffers.value = offersData?.nbHits ?? 0;
    nbSpaces.value = spacesCount ?? 0;

    stopLoading();
  };

  fetchSearchData();

  const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

  const mapIsVisible = ref<boolean>(!isMobileOrTablet.value);

  const hoverOfferHashId = ref<string | null>(null);

  const setHoverOfferHashId = (hashId: string | null): void => {
    hoverOfferHashId.value = hashId;
  };

  const openMap = (): void => {
    mapIsVisible.value = !mapIsVisible.value;
  };

  const closeMap = (): void => {
    mapIsVisible.value = false;
  };

  const getAggregatedOfferUrl = (offer: DeserializedOffer): Url | undefined => {
    if (!offer.is_aggregate) {
      return undefined;
    }

    const hashIds: string[] = offer.aggregated_offers.map((aggregatedOffer) => aggregatedOffer.hash_id);

    // @todo : capacity must be the value given in minimum capacity filter, @see https://bureaux-a-partager.atlassian.net/browse/MATCH-10559
    return `${offer.uri}?selected_offers=${hashIds.join(',')}&team-size=${offer.capacity}`;
  };

  return {
    isLoading,
    offers,
    nbOffers,
    nbSpaces,
    isFavorite,
    addFavorite,
    removeFavorite,
    mapIsVisible,
    openMap,
    closeMap,
    hoverOfferHashId,
    setHoverOfferHashId,
    perPage,
    transportLines,
    getAggregatedOfferUrl,
  };
});
